import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, descargar } from "../lib/api";
import { creditos, fechaHora, iso } from "../lib/formato";
import {
  Aviso,
  Cabecera,
  Campo,
  Cargando,
  Etiqueta,
  Modal,
  Panel,
  TarjetaDato,
  ThOrden,
  Vacio,
} from "../componentes/piezas";
import BuscadorSocios, { type SocioResumen } from "../componentes/BuscadorSocios";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Dos listas que se cuadran a la vez y que no son lo mismo:
 *
 *  - **Donaciones**: lo que alguien da al club aparte de su cuota y de lo que
 *    se lleva. Es dinero del club: entra y se queda.
 *  - **Ingresos en cartera**: lo que la gente mete en la entrada para gastarlo
 *    dentro. Es dinero que el club **debe**: el socio puede gastarlo o pedirlo
 *    de vuelta.
 *
 * Van en la misma pantalla porque se revisan juntas, y en pestañas separadas
 * porque sumarlas daría unos ingresos que la asociación no ha tenido.
 */

interface Donacion {
  id: string;
  numero: number;
  fecha: string;
  importeCentimos: number;
  metodoPago: string;
  concepto: string | null;
  anulada: boolean;
  motivoAnulacion: string | null;
  socio: { id: string; numeroSocio: number; nombre: string; apellidos: string } | null;
  usuario: { nombre: string } | null;
  puesto: { id: string; nombre: string } | null;
}

interface Recarga {
  id: string;
  fecha: string;
  importeCentimos: number;
  saldoResultante: number;
  metodoPago: string | null;
  concepto: string | null;
  socio: { id: string; numeroSocio: number; nombre: string; apellidos: string } | null;
  usuario: { nombre: string } | null;
  puesto: { id: string; nombre: string } | null;
}

interface RespuestaDonaciones {
  donaciones: Donacion[];
  total: number;
  anonimas: number;
}

interface RespuestaRecargas {
  recargas: Recarga[];
  total: number;
  porMetodo: Record<string, number>;
  saldoVivoCentimos: number;
}

type Pestana = "donaciones" | "cartera";
type Columna = "numero" | "fecha" | "quien" | "forma" | "puesto" | "importe";

/*
 * Las formas de pago de cada pestaña. En una donación cabe «de su cartera»
 * -el socio dona lo que tenía dentro-; en un ingreso no, porque el ingreso es
 * justo lo que llena la cartera.
 */
const METODOS_DONACION = ["EFECTIVO", "TARJETA", "TRANSFERENCIA", "CARTERA"];
const METODOS_RECARGA = ["EFECTIVO", "TARJETA", "TRANSFERENCIA"];

export default function Donaciones() {
  const { t, cuenta } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const primeroDeMes = new Date();
  primeroDeMes.setDate(1);

  const [pestana, setPestana] = useState<Pestana>("donaciones");
  const [desde, setDesde] = useState(iso(primeroDeMes));
  const [hasta, setHasta] = useState(iso(new Date()));
  const [buscar, setBuscar] = useState("");
  const [metodoPago, setMetodoPago] = useState("");
  const [puestoId, setPuestoId] = useState("");
  const [incluirAnuladas, setIncluirAnuladas] = useState(true);
  const [orden, setOrden] = useState<Columna>("fecha");
  const [descendente, setDescendente] = useState(true);
  const [registrando, setRegistrando] = useState(false);
  const [anulando, setAnulando] = useState<Donacion | null>(null);
  const [error, setError] = useState("");

  const comun = {
    desde,
    hasta,
    buscar: buscar.trim() || undefined,
    metodoPago: metodoPago || undefined,
    puestoId: puestoId || undefined,
  };

  const donaciones = useQuery({
    queryKey: ["donaciones", comun, incluirAnuladas],
    queryFn: () =>
      api<RespuestaDonaciones>("/donaciones", {
        query: { ...comun, incluirAnuladas: incluirAnuladas ? undefined : "false" },
      }),
    enabled: pestana === "donaciones",
  });

  const recargas = useQuery({
    queryKey: ["recargas", comun],
    queryFn: () => api<RespuestaRecargas>("/cartera/recargas", { query: comun }),
    enabled: pestana === "cartera",
  });

  const puestos = useQuery({
    queryKey: ["puestos"],
    queryFn: () => api<{ id: string; nombre: string }[]>("/club/puestos"),
    enabled: puede("ajustesClub"),
  });
  const hayPuestos = (puestos.data?.length ?? 0) > 0;

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["donaciones"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["recargas"] });
    // El dinero que entra toca la caja y el informe: hay que refrescarlos.
    void clienteConsultas.invalidateQueries({ queryKey: ["caja"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["panel"] });
  };

  const ordenarPor = (c: Columna) => {
    if (c === orden) setDescendente((d) => !d);
    else {
      setOrden(c);
      setDescendente(c === "importe" || c === "fecha" || c === "numero");
    }
  };

  /** Ordena cualquiera de las dos listas por la columna elegida. */
  function ordenar<T extends Donacion | Recarga>(lista: T[]) {
    const valor = (f: T): string | number => {
      switch (orden) {
        case "numero":
          return "numero" in f ? f.numero : f.fecha;
        case "quien":
          return f.socio ? `${f.socio.nombre} ${f.socio.apellidos}`.toLowerCase() : "";
        case "forma":
          return f.metodoPago ?? "";
        case "puesto":
          return f.puesto?.nombre.toLowerCase() ?? "";
        case "importe":
          return f.importeCentimos;
        default:
          return f.fecha;
      }
    };
    return [...lista].sort((a, b) => {
      const x = valor(a);
      const y = valor(b);
      const cmp =
        typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "es");
      return descendente ? -cmp : cmp;
    });
  }

  const filasDonaciones = useMemo(
    () => ordenar(donaciones.data?.donaciones ?? []),
    [donaciones.data, orden, descendente],
  );
  const filasRecargas = useMemo(
    () => ordenar(recargas.data?.recargas ?? []),
    [recargas.data, orden, descendente],
  );

  const metodos = pestana === "donaciones" ? METODOS_DONACION : METODOS_RECARGA;
  const cargando = pestana === "donaciones" ? donaciones.isLoading : recargas.isLoading;

  return (
    <>
      <Cabecera
        titulo={t("do.titulo")}
        descripcion={t("do.descripcion")}
      >
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 160 }} />
        <span className="suave">{t("do.a")}</span>
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 160 }} />
        {puede("verInformes") && (
          <button
            className="boton secundario"
            onClick={() =>
              descargar(
                `/exportar/${pestana === "donaciones" ? "donaciones" : "recargas"}.csv?desde=${desde}&hasta=${hasta}`,
                `${pestana === "donaciones" ? "donaciones" : "ingresos-cartera"}-${desde}-a-${hasta}.csv`,
              ).catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("do.falloExportar")))
            }
          >
            {t("do.exportar")}
          </button>
        )}
        {pestana === "donaciones" && puede("gestionarGastos") && (
          <button className="boton" onClick={() => setRegistrando(true)}>
            {t("do.nueva")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      <div className="pestanas">
        {(
          [
            ["donaciones", t("do.tabDonaciones")],
            ["cartera", t("do.tabCartera")],
          ] as [Pestana, string][]
        ).map(([clave, texto]) => (
          <button
            key={clave}
            className={`pestana${pestana === clave ? " activa" : ""}`}
            onClick={() => {
              setPestana(clave);
              setMetodoPago("");
              setOrden("fecha");
              setDescendente(true);
            }}
          >
            {texto}
          </button>
        ))}
      </div>

      <div className="rejilla tres" style={{ marginBottom: 16 }}>
        {pestana === "donaciones" ? (
          <>
            <TarjetaDato
              etiqueta={t("do.donadoEnPeriodo")}
              valor={creditos(donaciones.data?.total ?? 0)}
              pie={cuenta(
                filasDonaciones.filter((d) => !d.anulada).length,
                "do.unaDonacion",
                "do.donaciones",
              )}
            />
            <TarjetaDato
              etiqueta={t("do.sinNombre")}
              valor={String(donaciones.data?.anonimas ?? 0)}
              pie={t("do.delBote")}
            />
            <TarjetaDato
              etiqueta={t("do.enEfectivo")}
              valor={creditos(
                filasDonaciones
                  .filter((d) => !d.anulada && d.metodoPago === "EFECTIVO")
                  .reduce((s, d) => s + d.importeCentimos, 0),
              )}
              pie={t("do.entraEnCaja")}
            />
          </>
        ) : (
          <>
            <TarjetaDato
              etiqueta={t("do.ingresadoEnPeriodo")}
              valor={creditos(recargas.data?.total ?? 0)}
              pie={cuenta(filasRecargas.length, "do.unIngreso", "do.ingresos")}
            />
            <TarjetaDato
              etiqueta={t("do.clubDebe")}
              valor={creditos(recargas.data?.saldoVivoCentimos ?? 0)}
              pie={t("do.saldoVivo")}
            />
            <TarjetaDato
              etiqueta={t("do.enEfectivo")}
              valor={creditos(recargas.data?.porMetodo.EFECTIVO ?? 0)}
              pie={t("do.entraEnCaja")}
            />
          </>
        )}
      </div>

      <Panel sinCuerpo>
        <div className="filtros-gastos">
          <input
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder={t("do.buscar")}
          />
          <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
            <option value="">{t("do.cualquierForma")}</option>
            {metodos.map((m) => (
              <option key={m} value={m}>
                {t(`pago.${m}` as Clave)}
              </option>
            ))}
          </select>
          {hayPuestos && (
            <select value={puestoId} onChange={(e) => setPuestoId(e.target.value)}>
              <option value="">{t("do.cualquierPuesto")}</option>
              {(puestos.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          )}
          {pestana === "donaciones" && (
            <label className="casilla">
              <input
                type="checkbox"
                checked={incluirAnuladas}
                onChange={(e) => setIncluirAnuladas(e.target.checked)}
              />
              <span>{t("do.verAnuladas")}</span>
            </label>
          )}
        </div>

        {cargando ? (
          <Cargando />
        ) : pestana === "donaciones" ? (
          filasDonaciones.length === 0 ? (
            <Vacio>{t("do.sinDonaciones")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <ThOrden columna="numero" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("do.numero")}
                    </ThOrden>
                    <ThOrden columna="fecha" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("do.fecha")}
                    </ThOrden>
                    <ThOrden columna="quien" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("do.quien")}
                    </ThOrden>
                    <th>{t("do.concepto")}</th>
                    <ThOrden columna="forma" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("do.forma")}
                    </ThOrden>
                    {hayPuestos && (
                      <ThOrden columna="puesto" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                        {t("do.puesto")}
                      </ThOrden>
                    )}
                    <th>{t("do.atendio")}</th>
                    <ThOrden
                      columna="importe"
                      orden={orden}
                      descendente={descendente}
                      alOrdenar={ordenarPor}
                      num
                    >
                      {t("do.importe")}
                    </ThOrden>
                    {puede("gestionarGastos") && <th />}
                  </tr>
                </thead>
                <tbody>
                  {filasDonaciones.map((d) => (
                    <tr key={d.id} className={d.anulada ? "anulada" : undefined}>
                      <td className="suave pequeno tabular">#{d.numero}</td>
                      <td className="pequeno suave">{fechaHora(d.fecha)}</td>
                      <td>
                        {d.socio ? (
                          <strong>
                            #{d.socio.numeroSocio} {d.socio.nombre} {d.socio.apellidos}
                          </strong>
                        ) : (
                          <span className="suave">{t("do.anonima")}</span>
                        )}
                        {d.anulada && <Etiqueta tipo="roja">{t("do.anulada")}</Etiqueta>}
                      </td>
                      <td className="pequeno">
                        {d.concepto ?? "—"}
                        {d.anulada && d.motivoAnulacion && (
                          <div className="suave pequeno">{d.motivoAnulacion}</div>
                        )}
                      </td>
                      <td className="pequeno">{t(`pago.${d.metodoPago}` as Clave)}</td>
                      {hayPuestos && <td className="pequeno">{d.puesto?.nombre ?? "—"}</td>}
                      <td className="pequeno suave">{d.usuario?.nombre ?? "—"}</td>
                      <td className="num tabular">{creditos(d.importeCentimos)}</td>
                      {puede("gestionarGastos") && (
                        <td style={{ textAlign: "right" }}>
                          {!d.anulada && (
                            <button className="boton fantasma" onClick={() => setAnulando(d)}>
                              {t("do.anular")}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : filasRecargas.length === 0 ? (
          <Vacio>{t("do.sinIngresos")}</Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <ThOrden columna="fecha" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                    {t("do.fecha")}
                  </ThOrden>
                  <ThOrden columna="quien" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                    {t("do.socio")}
                  </ThOrden>
                  <ThOrden columna="forma" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                    {t("do.forma")}
                  </ThOrden>
                  {hayPuestos && (
                    <ThOrden columna="puesto" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("do.puesto")}
                    </ThOrden>
                  )}
                  <th>{t("do.loCobro")}</th>
                  <ThOrden columna="importe" orden={orden} descendente={descendente} alOrdenar={ordenarPor} num>
                    {t("do.ingresado")}
                  </ThOrden>
                  <th className="num">{t("do.saldoDespues")}</th>
                </tr>
              </thead>
              <tbody>
                {filasRecargas.map((r) => (
                  <tr key={r.id}>
                    <td className="pequeno suave">{fechaHora(r.fecha)}</td>
                    <td>
                      {r.socio ? (
                        <strong>
                          #{r.socio.numeroSocio} {r.socio.nombre} {r.socio.apellidos}
                        </strong>
                      ) : (
                        <span className="suave">—</span>
                      )}
                    </td>
                    <td className="pequeno">
                      {r.metodoPago ? (
                        t(`pago.${r.metodoPago}` as Clave)
                      ) : (
                        /* Los de antes de que se guardara: no se inventa cuál fue. */
                        <span className="suave">{t("do.sinDato")}</span>
                      )}
                    </td>
                    {hayPuestos && <td className="pequeno">{r.puesto?.nombre ?? "—"}</td>}
                    <td className="pequeno suave">{r.usuario?.nombre ?? "—"}</td>
                    <td className="num tabular">{creditos(r.importeCentimos)}</td>
                    <td className="num tabular suave">{creditos(r.saldoResultante)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {pestana === "cartera" && filasRecargas.length > 0 && (
        <p className="suave pequeno" style={{ marginTop: 12 }}>
          Lo ingresado no es un ingreso de la asociación: es dinero que el club debe hasta que el socio lo
          gaste o lo pida de vuelta. Por eso va aparte de las donaciones.
        </p>
      )}

      {registrando && (
        <DialogoDonacion
          alCerrar={() => setRegistrando(false)}
          alFallar={setError}
          alHecho={() => {
            setRegistrando(false);
            setError("");
            refrescar();
          }}
        />
      )}

      {anulando && (
        <DialogoAnular
          donacion={anulando}
          alCerrar={() => setAnulando(null)}
          alFallar={setError}
          alHecho={() => {
            setAnulando(null);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function DialogoDonacion({
  alCerrar,
  alHecho,
  alFallar,
}: {
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [socio, setSocio] = useState<SocioResumen | null>(null);
  const [importeEuros, setImporteEuros] = useState("");
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [concepto, setConcepto] = useState("");
  const [enviando, setEnviando] = useState(false);

  const importe = Number(importeEuros.replace(",", "."));
  const valido = importe > 0 && (metodoPago !== "CARTERA" || socio !== null);

  return (
    <Modal
      titulo={t("do.r.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !valido}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/donaciones", {
                  metodo: "POST",
                  cuerpo: {
                    socioId: socio?.id ?? null,
                    importeEuros: importe,
                    metodoPago,
                    concepto: concepto.trim() || undefined,
                  },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("do.r.fallo"));
                setEnviando(false);
              }
            }}
          >
            {enviando ? t("g.guardando") : t("do.r.registrar")}
          </button>
        </>
      }
    >
      <Campo
        etiqueta={t("do.r.quienDona")}
        ayuda={t("do.r.quienDonaAyuda")}
      >
        {socio ? (
          <div className="fila separada">
            <span>
              #{socio.numeroSocio} {socio.nombre} {socio.apellidos}
            </span>
            <button className="boton fantasma" onClick={() => setSocio(null)}>
              {t("do.r.quitar")}
            </button>
          </div>
        ) : (
          <BuscadorSocios alElegir={setSocio} marcador={t("do.r.buscarSocio")} />
        )}
      </Campo>

      <div className="linea-campos">
        <Campo etiqueta={t("do.r.importe")}>
          <input
            type="number"
            step="0.01"
            min={0}
            value={importeEuros}
            onChange={(e) => setImporteEuros(e.target.value)}
            autoFocus
          />
        </Campo>
        <Campo etiqueta={t("do.r.formaDePago")}>
          <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
            {METODOS_DONACION.map((m) => (
              <option key={m} value={m}>
                {t(`pago.${m}` as Clave)}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      {metodoPago === "CARTERA" && !socio && (
        <Aviso tipo="atencion">{t("do.r.faltaSocio")}</Aviso>
      )}
      {metodoPago === "EFECTIVO" && (
        <p className="suave pequeno">{t("do.r.efectivoNota")}</p>
      )}

      <Campo etiqueta={t("do.r.concepto")} ayuda={t("do.r.conceptoAyuda")}>
        <input value={concepto} onChange={(e) => setConcepto(e.target.value)} maxLength={200} />
      </Campo>
    </Modal>
  );
}

function DialogoAnular({
  donacion,
  alCerrar,
  alHecho,
  alFallar,
}: {
  donacion: Donacion;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("do.an.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton peligro"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/donaciones/${donacion.id}/anular`, { metodo: "POST", cuerpo: { motivo } });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("do.an.fallo"));
                setEnviando(false);
              }
            }}
          >
            {enviando ? t("do.an.anulando") : t("do.an.anular")}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>
        {t("do.an.explicacion", { importe: creditos(donacion.importeCentimos) })}
      </p>
      {donacion.metodoPago === "EFECTIVO" && (
        <p className="suave pequeno">{t("do.an.efectivo")}</p>
      )}
      {donacion.metodoPago === "CARTERA" && (
        <p className="suave pequeno">{t("do.an.cartera")}</p>
      )}

      <Campo etiqueta={t("do.an.motivo")} ayuda={t("do.an.motivoAyuda")}>
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={300} autoFocus />
      </Campo>
    </Modal>
  );
}
